/**
 * 「目录里可能已有」的询问队列：任何地方（含非组件的工具函数，如 `createProductFromName`、
 * 批量建采购）`await askProductChoice(...)`，由挂在 Providers 里的 `ProductChoiceHost` 弹窗回答。
 *
 * 一次只弹一个；后来的排队。用户关掉弹窗 = 回 null（调用方当作取消这一行）。
 */
import { useSyncExternalStore } from 'react';
import type { ProductChoice, CatalogCandidate } from './catalogGuard';
import type { SnowflakeId } from '@/types/snowflake';

/**
 * - `product`：建产品被拦（新的 → 带 confirmNew 重建）；
 * - `purchase`：建采购行配不准产品（2026-10-29 起后端不再建待审产品）：
 *   `new` = 打开建产品抽屉建好再带 productId 重发。没有自由文本（Frank 10-30）。
 */
export type ProductChoiceMode = 'product' | 'purchase';

export type ProductChoiceAnswer =
  | { kind: 'use'; product: CatalogCandidate }
  /** `notThese` = 用户看过、排除掉的候选。 */
  | { kind: 'new'; notThese: SnowflakeId[] };

/** 批量里第几个问题（1 起）/ 这一轮一共几个。 */
export interface ProductChoiceProgress {
  index: number;
  total: number;
}

export interface ProductChoiceRequest {
  id: number;
  choice: ProductChoice;
  mode: ProductChoiceMode;
  progress?: ProductChoiceProgress;
  resolve: (answer: ProductChoiceAnswer | null) => void;
}

let queue: ProductChoiceRequest[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function askProductChoice(
  choice: ProductChoice,
  mode: ProductChoiceMode,
  progress?: ProductChoiceProgress,
): Promise<ProductChoiceAnswer | null> {
  return new Promise((resolve) => {
    queue = [...queue, { id: nextId++, choice, mode, progress, resolve }];
    emit();
  });
}

/** 回答队首那一个（Host 调）。 */
export function answerProductChoice(id: number, answer: ProductChoiceAnswer | null) {
  const req = queue.find((r) => r.id === id);
  if (!req) return;
  queue = queue.filter((r) => r.id !== id);
  emit();
  req.resolve(answer);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const current = () => queue[0] ?? null;

export function useCurrentProductChoice(): ProductChoiceRequest | null {
  return useSyncExternalStore(subscribe, current, current);
}

// ── 建产品请求：采购行「都不是」→ 打开建产品抽屉（非组件代码也能 await） ──────────────

export interface CreateProductRequest {
  id: number;
  /** 预填抽屉的描述（用户在采购行上写的那句） */
  text: string
  /** 批量时第几个 / 共几个（抽屉标题显示） */
  progress?: ProductChoiceProgress;
  resolve: (product: { id: SnowflakeId; name: string } | null) => void;
}

let createQueue: CreateProductRequest[] = [];
const createListeners = new Set<() => void>();

/** 打开建产品抽屉；建好回产品，关掉回 null。 */
export function askCreateProduct(text: string, progress?: ProductChoiceProgress): Promise<{ id: SnowflakeId; name: string } | null> {
  return new Promise((resolve) => {
    createQueue = [...createQueue, { id: nextId++, text, progress, resolve }];
    createListeners.forEach((l) => l());
  });
}

export function answerCreateProduct(id: number, product: { id: SnowflakeId; name: string } | null) {
  const req = createQueue.find((r) => r.id === id);
  if (!req) return;
  createQueue = createQueue.filter((r) => r.id !== id);
  createListeners.forEach((l) => l());
  req.resolve(product);
}

const currentCreate = () => createQueue[0] ?? null;

function subscribeCreate(listener: () => void) {
  createListeners.add(listener);
  return () => createListeners.delete(listener);
}

export function useCurrentCreateProduct(): CreateProductRequest | null {
  return useSyncExternalStore(subscribeCreate, currentCreate, currentCreate);
}
